import PropTypes from 'prop-types';
import {
  getUsageColor,
  getRemaining,
  isLimitReached,
  SUPPORT_EMAIL
} from '../../utils/constants/subscription';
import { DOWNLOAD_LIMIT_REACHED_MESSAGE } from '../../utils/constants/downloadLimits';
import LimitIndicator from './LimitIndicator';
import { formatPeriodDate } from '../../utils/contractPeriods';

// `label` defaults to the full "Downloads remaining" for back-compat with
// non-popup callers (subscription card, etc.). Popup mounts pass the
// shorter "Downloads" so the label aligns with the download buttons below
// without wrapping.
// TD-2813: `resetsOn` is the day the NEXT contract period starts, when the
// allowance comes back. Null for a company with a single period — its downloads
// never reset, and saying they do would be a promise nobody made.
const DownloadCounter = ({ used = 0, limit, label = 'Downloads remaining', resetsOn = null }) => {
  // Don't render for unlimited or unconfigured
  if (limit == null || limit <= 0) return null;

  const remaining = getRemaining(used, limit);
  const color = getUsageColor(used, limit);
  const limitHit = isLimitReached(used, limit);
  const resetNote = resetsOn ? (
    <div className="download-counter-reset">Resets on {formatPeriodDate(resetsOn)}</div>
  ) : null;

  if (limitHit) {
    return (
      <div className="download-counter download-counter-red" role="alert">
        <div className="download-counter-label">{label}</div>
        <div
          className="download-counter-value"
          title={`${used.toLocaleString()} of ${limit.toLocaleString()} downloads used`}
        >
          0 / {limit.toLocaleString()}
        </div>
        <LimitIndicator
          limitReached={true}
          message={DOWNLOAD_LIMIT_REACHED_MESSAGE}
          contactEmail={SUPPORT_EMAIL}
        />
        {/* Worth most exactly here: a customer who has run out wants the date
            their allowance comes back before they want a support address. */}
        {resetNote}
      </div>
    );
  }

  return (
    <div className={`download-counter download-counter-${color}`} role="status" aria-live="polite">
      <div className="download-counter-label">{label}</div>
      <div
        className="download-counter-value"
        title={`${used.toLocaleString()} of ${limit.toLocaleString()} downloads used`}
      >
        {remaining.toLocaleString()} / {limit.toLocaleString()}
      </div>
      {resetNote}
    </div>
  );
};

DownloadCounter.propTypes = {
  used: PropTypes.number,
  limit: PropTypes.number,
  label: PropTypes.string,
  resetsOn: PropTypes.string
};

export default DownloadCounter;
